import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Chip,
  Alert,
  Card,
  CardContent,
  useTheme,
  useMediaQuery
} from '@mui/material';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import axios from 'axios';

// 🔧 価格情報取得フック - 既存中間テーブル用
export const usePriceInfo = (selectedProduct, refreshTrigger = 0) => {
  const [priceData, setPriceData] = useState({
    customPrice: null,        // 顧客専用単価
    standardPrice: null,      // 製品標準単価
    loading: true,
    error: null
  });

  const fetchPriceInfo = useCallback(async () => {
    if (!selectedProduct?.id) {
      setPriceData({ 
        customPrice: null, 
        standardPrice: null, 
        loading: false,
        error: null 
      });
      return;
    }
    
    setPriceData(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const token = sessionStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // 1. 顧客製品情報を取得
      const clientProductResponse = await axios.get(`/api/client-products/${selectedProduct.id}`, config);
      const clientProduct = clientProductResponse.data;
      
      // 2. 製品の標準単価を取得
      let standardPrice = null;
      if (clientProduct.productId) {
        try {
          const standardPriceResponse = await axios.get(`/api/products/${clientProduct.productId}/standard-unit-price`, config);
          standardPrice = standardPriceResponse.data;
        } catch (productError) {
        }
      }
      
      setPriceData({
        customPrice: clientProduct.unitPrice,
        standardPrice: standardPrice,
        loading: false,
        error: null
      });
      
    } catch (error) {
      setPriceData(prev => ({ 
        ...prev, 
        loading: false,
        error: '単価情報の取得に失敗しました'
      }));
    }
  }, [selectedProduct?.id]);

  useEffect(() => {
    fetchPriceInfo();
  }, [fetchPriceInfo, refreshTrigger]);

  return { ...priceData, refetch: fetchPriceInfo };
};

// 🔧 独立した価格情報取得フック - productIdベース（予測未設定時用）
export const usePriceInfoByProductId = (productId, refreshTrigger = 0) => {
  const [priceData, setPriceData] = useState({
    standardPrice: null,
    loading: true,
    error: null
  });

  const fetchPriceInfo = useCallback(async () => {
    if (!productId) {
      setPriceData({ 
        standardPrice: null, 
        loading: false,
        error: null 
      });
      return;
    }
    
    setPriceData(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const token = sessionStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const standardPriceResponse = await axios.get(`/api/products/${productId}/standard-unit-price`, config);
      const standardPrice = standardPriceResponse.data;
      
      setPriceData({
        standardPrice: standardPrice,
        loading: false,
        error: null
      });
      
    } catch (error) {
      setPriceData(prev => ({ 
        ...prev, 
        loading: false,
        error: '製品標準単価の取得に失敗しました'
      }));
    }
  }, [productId]);

  useEffect(() => {
    fetchPriceInfo();
  }, [fetchPriceInfo, refreshTrigger]);

  return { ...priceData, refetch: fetchPriceInfo };
};

// 価格フォーマット関数
export const formatPrice = (price) => {
  if (price === null || price === undefined) return null;
  return `¥${Number(price).toLocaleString()}`;
};

// 🆕 価格表示コンポーネント - 既存中間テーブル用とproductIdのみ用の統合版
export const PriceInfoDisplay = ({ 
  selectedProduct, 
  productId, 
  onPriceEdit, 
  refreshTrigger,
  clientId // 🆕 新規作成時に必要
}) => {
  // selectedProductがある場合とproductIdのみの場合を分岐
  const shouldUseProductIdHook = !selectedProduct && productId;
  
  // selectedProductがある場合の価格情報
  const productPriceInfo = usePriceInfo(selectedProduct, refreshTrigger);
  
  // productIdのみの場合の価格情報  
  const standardPriceInfo = usePriceInfoByProductId(
    shouldUseProductIdHook ? productId : null, 
    refreshTrigger
  );
  
  // 適切な価格情報を選択
  const { customPrice, standardPrice, loading, error, refetch } = shouldUseProductIdHook 
    ? { 
        customPrice: null, 
        standardPrice: standardPriceInfo.standardPrice, 
        loading: standardPriceInfo.loading, 
        error: standardPriceInfo.error, 
        refetch: standardPriceInfo.refetch 
      }
    : productPriceInfo;

  const [showDetails, setShowDetails] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (error) {
    return (
      <Alert severity="error" action={
        <Button color="inherit" size="small" onClick={refetch}>
          再試行
        </Button>
      }>
        {error}
      </Alert>
    );
  }

  if (loading) {
    return (
      <Card variant="outlined">
        <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
          <Typography variant="body2" color="text.secondary">
            単価情報を読み込み中...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // 適用価格の判定
  const isUsingCustomPrice = customPrice !== null;
  const appliedPrice = isUsingCustomPrice ? customPrice : standardPrice;
  const hasAnyPrice = appliedPrice !== null;

  // 🆕 編集ボタンのハンドラー - 中間テーブルの有無で処理を分岐
  const handlePriceEditClick = () => {
    if (onPriceEdit) {
      onPriceEdit({
        mode: selectedProduct ? 'edit' : 'create',
        selectedProduct,
        productId,
        clientId
      });
    }
  };

  // 🔹 スマホ版：コンパクト表示
  if (isMobile) {
    return (
      <Card variant="outlined" sx={{ 
        borderColor: isUsingCustomPrice ? 'success.main' : 
                     hasAnyPrice ? 'primary.main' : 'warning.main',
        bgcolor: isUsingCustomPrice ? 'success.50' : 
                 hasAnyPrice ? 'primary.50' : 'warning.50',
        mb: 2
      }}>
        <CardContent sx={{ p: 1.5 }}>
          {/* スマホ版メインヘッダー */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isUsingCustomPrice ? (
                <LocalOfferIcon color="success" sx={{ fontSize: 20 }} />
              ) : hasAnyPrice ? (
                <MonetizationOnIcon color="primary" sx={{ fontSize: 20 }} />
              ) : (
                <MonetizationOnIcon color="warning" sx={{ fontSize: 20 }} />
              )}
              <Typography variant="body2" color="text.secondary" fontWeight="medium">
                適用単価
              </Typography>
            </Box>
            
            <Button
              size="small"
              variant="outlined"
              onClick={handlePriceEditClick}
              sx={{ 
                minWidth: 'auto',
                px: 1.5,
                py: 0.5,
                fontSize: '0.75rem'
              }}
            >
              編集
            </Button>
          </Box>
          
          {/* スマホ版価格とステータス */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" fontWeight="bold" color={hasAnyPrice ? 'text.primary' : 'warning.main'}>
              {hasAnyPrice ? formatPrice(appliedPrice) : '未設定'}
            </Typography>
            
            {isUsingCustomPrice ? (
              <Chip 
                label="専用単価" 
                color="success" 
                size="small"
                sx={{ 
                  height: 20, 
                  fontSize: '0.7rem',
                  fontWeight: 'bold',
                  '& .MuiChip-label': { px: 0.8 }
                }}
              />
            ) : hasAnyPrice ? (
              <Chip 
                label="標準単価" 
                color="primary" 
                size="small"
                variant="outlined"
                sx={{ 
                  height: 20, 
                  fontSize: '0.7rem',
                  '& .MuiChip-label': { px: 0.8 }
                }}
              />
            ) : (
              <Chip 
                label="要設定" 
                color="warning" 
                size="small"
                variant="outlined"
                sx={{ 
                  height: 20, 
                  fontSize: '0.7rem',
                  '& .MuiChip-label': { px: 0.8 }
                }}
              />
            )}
          </Box>

          {/* スマホ版詳細の折りたたみ */}
          <Button
            size="small"
            onClick={() => setShowDetails(!showDetails)}
            endIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ 
              p: 0,
              minHeight: 'auto',
              fontSize: '0.75rem',
              color: 'text.secondary',
              textTransform: 'none'
            }}
          >
            詳細{showDetails ? '非表示' : '表示'}
          </Button>

          {/* スマホ版詳細情報（折りたたみ式） */}
          {showDetails && (
            <Box sx={{ 
              mt: 1.5,
              pt: 1.5, 
              borderTop: '1px solid', 
              borderColor: 'divider',
              bgcolor: 'background.paper',
              borderRadius: 1,
              p: 1
            }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                単価設定詳細
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption">標準単価:</Typography>
                  <Typography variant="caption" fontWeight="medium">
                    {standardPrice !== null ? formatPrice(standardPrice) : '未設定'}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color={isUsingCustomPrice ? 'success.main' : 'text.disabled'}>
                    専用単価:
                  </Typography>
                  <Typography 
                    variant="caption" 
                    fontWeight={isUsingCustomPrice ? 'bold' : 'normal'}
                    color={isUsingCustomPrice ? 'success.main' : 'text.disabled'}
                  >
                    {isUsingCustomPrice ? `${formatPrice(customPrice)} ✓` : '未設定'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  }

  // 🔹 PC版：従来の表示を維持
  return (
    <Card variant="outlined" sx={{ 
      borderColor: isUsingCustomPrice ? 'success.main' : 
                   hasAnyPrice ? 'primary.main' : 'warning.main',
      bgcolor: isUsingCustomPrice ? 'success.50' : 
               hasAnyPrice ? 'primary.50' : 'warning.50',
      mb: 2
    }}>
      <CardContent sx={{ p: 2 }}>
        {/* ヘッダー部分 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isUsingCustomPrice ? (
              <LocalOfferIcon color="success" />
            ) : hasAnyPrice ? (
              <MonetizationOnIcon color="primary" />
            ) : (
              <MonetizationOnIcon color="warning" />
            )}
            <Typography variant="body2" color="text.secondary" fontWeight="medium">
              適用単価
            </Typography>
          </Box>
          
          <Button
            size="small"
            variant="outlined"
            onClick={handlePriceEditClick}
          >
            編集
          </Button>
        </Box>
        
        {/* メイン価格表示 */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" fontWeight="bold" color={hasAnyPrice ? 'text.primary' : 'warning.main'}>
            {hasAnyPrice ? formatPrice(appliedPrice) : '単価未設定'}
          </Typography>
          
          <Box sx={{ mt: 1 }}>
            {isUsingCustomPrice ? (
              <Chip 
                label="顧客専用単価" 
                color="success" 
                size="small"
                sx={{ fontWeight: 'bold' }}
              />
            ) : hasAnyPrice ? (
              <Chip 
                label="製品標準単価" 
                color="primary" 
                size="small"
                variant="outlined"
              />
            ) : (
              <Chip 
                label="単価設定が必要です" 
                color="warning" 
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        </Box>

        {/* 価格詳細情報 */}
        <Box sx={{ 
          pt: 1, 
          borderTop: '1px solid', 
          borderColor: 'divider',
          bgcolor: 'background.paper',
          borderRadius: 1,
          p: 1.5
        }}>
          {isUsingCustomPrice ? (
            // 専用価格使用時：標準価格も併記
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                単価設定詳細
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">製品標準単価:</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {standardPrice !== null ? formatPrice(standardPrice) : '未設定'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="success.main">顧客専用単価:</Typography>
                <Typography variant="body2" fontWeight="bold" color="success.main">
                  {formatPrice(customPrice)} ✓
                </Typography>
              </Box>
            </Box>
          ) : (
            // 標準価格使用時または未設定時
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                単価設定詳細
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">製品標準単価:</Typography>
                <Typography variant="body2" fontWeight="medium" color={hasAnyPrice ? 'primary.main' : 'warning.main'}>
                  {standardPrice !== null ? `${formatPrice(standardPrice)} ✓` : '未設定'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">顧客専用単価:</Typography>
                <Typography variant="body2" color="text.disabled">
                  未設定
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};